import Grid from "@mui/material/Grid2";
import { isNil, pipe, pluck, reject } from "ramda";
import { isNotNil } from "ramda-adjunct";
import React, { forwardRef, ReactNode, useCallback } from "react";
import { CommonError } from "@sinch/components/CommonError/CommonError";
import { useMultilevelSelect } from "@sinch/components/form/MultilevelSelect/hooks/useMultilevelSelect";
import { ListColumn } from "@sinch/components/form/MultilevelSelect/ListColumn";
import { ListSearchField } from "@sinch/components/form/MultilevelSelect/ListSearchField";
import { ListColumnProps, NestedOptions, RenderedOptions } from "@sinch/components/form/MultilevelSelect/types";

interface MultilevelSelectProps<TValue = any> {
  value: TValue | null;
  onChange?: (e: React.UIEvent<HTMLElement>, value: TValue) => void;
  options: NestedOptions<TValue>[];
  getListItemProps?: (value: TValue) => any;
  enterListEvent?: "click" | "mouseOver";
  onItemHover?: (index: number[]) => void;
  onMouseOver?: (index: number[]) => void;
  renderItem?: (item: Pick<RenderedOptions<TValue>, "value" | "label">) => ReactNode;
}

const MultilevelSelectBase = <TValue,>(
  {
    value,
    onChange,
    options,
    getListItemProps,
    enterListEvent = "mouseOver",
    onMouseOver,
    onItemHover,
    renderItem,
    ...props
  }: MultilevelSelectProps<TValue>,
  ref: React.Ref<HTMLDivElement>
) => {
  const { onSearch, focusPath, search, listColumn, loading, selectedPath, error } = useMultilevelSelect<TValue>({
    options,
    value,
    onChange,
  });

  const onListColumnMouseOver = useCallback(
    (columnIx: number) => (index: number) => {
      const hoverPath = pipe<ListColumnProps<TValue>[][], (number | null)[], number[]>(
        pluck("selected"),
        reject(isNil)
      )(listColumn);
      const newPath = [...hoverPath];
      newPath[columnIx] = index;
      onMouseOver?.(newPath.slice(0, columnIx + 1));
    },
    [listColumn, onMouseOver]
  );

  if (error) {
    return <CommonError />;
  }

  return (
    <Grid
      ref={ref}
      container
      direction="column"
      sx={{ height: "100%" }}
      wrap="nowrap"
      {...props}
      onKeyDown={(e) => {
        if (e.key === "ArrowDown" && focusPath.path[0] === null) {
          focusPath.setAt(0, 0);
        }
      }}
    >
      <Grid>
        <ListSearchField loading={loading} onChange={onSearch} value={search} />
      </Grid>
      <Grid container direction="row" onMouseOver={() => focusPath.reset()} sx={{ overflow: "hidden", height: "100%" }}>
        {listColumn.map(({ level, list, onKeyDown, onOpenList, onChange: handleChange, selected, atValuePath }, ix) => (
          <ListColumn<TValue>
            key={`level-${level}`}
            atValuePath={atValuePath}
            focusedId={
              focusPath.path.filter(isNotNil).length - 1 === ix && isNotNil(focusPath.path[ix])
                ? focusPath.path[ix]
                : null
            }
            getListItemProps={getListItemProps}
            list={list}
            onChange={handleChange}
            onClick={(index: number) => {
              if (enterListEvent === "click") {
                onOpenList(index);
              }
            }}
            onItemHover={(index: number) => {
              onItemHover?.(selectedPath);
              if (enterListEvent === "mouseOver") {
                onOpenList(index);
              }
            }}
            onKeyDown={(e) => onKeyDown(e, ix)}
            onMouseOver={onListColumnMouseOver(ix)}
            renderItem={renderItem}
            selectedIndex={selected}
            selectedValue={value}
          />
        ))}
      </Grid>
    </Grid>
  );
};

export const MultilevelSelect = forwardRef(MultilevelSelectBase) as <TValue>(
  props: MultilevelSelectProps<TValue> & { ref?: React.Ref<HTMLDivElement> }
) => React.JSX.Element;
